import { Component, OnDestroy, OnInit, signal, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MainStore } from 'src/app/common/main.store';
import { DbTemplateService } from '../db-template.service';
import { ToastrService } from 'ngx-toastr';
import { DbObjectItemCreatedByEnum, DbObjectTypeEnum, DbTemplate, DbTemplateInfo, DbTemplateItem } from '../db-template.model';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import validator from 'validator';
import { DbTemplateDeleteComponent } from '../db-template-delete/db-template-delete.component';
import { CommonService } from 'src/app/services/common.service';
import { Subject } from 'rxjs';
import { DbTemplateInfoComponent } from '../db-template-info/db-template-info.component';
import { DbTemplateFilterComponent } from '../db-template-filter/db-template-filter.component';
import { DbTemplatePropertiesComponent } from '../db-template-properties/db-template-properties.component';
import { DbTemplateSchemaDiffComponent } from '../db-template-schema-diff/db-template-schema-diff.component';
import { DbTemplateFieldCreateComponent } from '../db-template-field-create/db-template-field-create.component';
import { SearchInputComponent } from 'src/app/common/general/search-input/search-input.component';
import { FormsModule } from '@angular/forms';
import { ErrorService } from '../../../../common/errors/error.service';
import { HasUnsavedChanges } from 'src/app/guards/unsaved-changes.guard';

@Component({
    selector: 'app-db-template-detail',
    templateUrl: './db-template-detail.component.html',
    styleUrls: ['./db-template-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgbTooltip, FormsModule, SearchInputComponent]
})
export class DbTemplateDetailComponent implements OnInit, OnDestroy, HasUnsavedChanges {

  isAdminUser: boolean = false;
  isLoading: boolean = true;
  isEmpty: boolean = true;
  isEnabled: boolean = false;
  awaitingNetworkResponse: boolean = false;
  templateWorkingText: string = '';
  readdbtemplateInput: string = '';
  dbObjectTypeEnum: typeof DbObjectTypeEnum = DbObjectTypeEnum;

  propertiesInfo: string = '';  
  propertiesInfoTooltip: string = 'R: Læse, W: Skrive, D: Slette, Afdeling: hvad afdelingerne må med skabeloner baseret på databaseskabelonen';

  postProcessChoicesWrite: string[] = ['Ingen','Norm','Resources (Oprettelse af HR ResourceProfile)', 'Resources','Registrations','WageType'];
  postProcessChoicesDelete: string[] = ['Ingen','DeleteContract','DeleteWageType'];
  
  dbTemplateInfo: DbTemplateInfo;
  allDbObjectItems = signal<DbTemplateItem[]>([]);
  filterDbObjectItems = signal<DbTemplateItem[]>([]);

  dialogDbCaption: string = '';
  searchText = signal<string>('');
  selectedItem = signal<DbTemplateItem | null>(null);
  destroy$ = new Subject<void>();

  // dekorater for at tracke om der er ændret i skabelonen ift. originalen
  dbDbObjectItemCreatedByEnum = DbObjectItemCreatedByEnum;

  // Gemte feltnavne (id → navn), så omdøbte felter kan opdages ved gem.
  private savedFieldNames = new Map<string, string>();
  // Skabelonen som den sidst blev hentet/gemt, så ugemte ændringer kan opdages.
  private savedState = '';
  // Sat når skabelonen er slettet: der er intet at gemme, så navigationen væk må ikke advare.
  private leaveWithoutCheck = false;
  
    constructor(
      private store: MainStore,
      private router: Router,
      private route: ActivatedRoute,      
      private templateService: DbTemplateService,
      private toastrService: ToastrService,
      private ngmService: NgbModal,
      private commonService: CommonService,
      private errorService: ErrorService
  ) {
    this.isAdminUser = this.store.currentUser.get().isAdmin && this.store.currentUser.get().departmentCode == '200'
    this.dbTemplateInfo = {} as DbTemplateInfo;
    this.dbTemplateInfo.dbTemplate = {} as DbTemplate;
    this.dbTemplateInfo.dbTemplateItems = [];
  }


  async ngOnInit(): Promise<void> {
      if (this.isAdminUser) {
        const _templateId = this.route.snapshot.paramMap.get('templateid');
        if (_templateId == null || ! validator.isUUID(_templateId)) {
            this.errorService.notify('TemplateId ['+ this.route.snapshot.paramMap.get('templateid') + '] er ikke gyldigt!');
            this.router.navigate(['/home']);
        }
        else
        {
          await this.GetDbTemplate(_templateId);
          this.isLoading = false;
        }
      }
      else {
        this.errorService.notify('Adgang nægtet!', { tier: 'modal' });
        this.router.navigate(['/home']);
      }
  }
  
  async GetDbTemplate(templateId: string) {
      this.isLoading = true;  
      this.templateWorkingText = 'Henter skabelon..';
      this.dbTemplateInfo = await this.templateService.getDbTemplateInfo(templateId);
      this.setPropertiesInfo();

      this.dialogDbCaption = 'Underliggende databaseobjekt:';
      if (this.dbTemplateInfo.dbTemplate.dbObjectType == DbObjectTypeEnum.Table) {
        this.dialogDbCaption = 'Underliggende tabel:'
      } else if (this.dbTemplateInfo.dbTemplate.dbObjectType == DbObjectTypeEnum.View) {
        this.dialogDbCaption = 'Underliggende view:';
      } else if (this.dbTemplateInfo.dbTemplate.dbObjectType == DbObjectTypeEnum.StoredProcedure) {
        this.dialogDbCaption = 'Underliggende stored procedure:';
      }
      this.dialogDbCaption += ' [' + this.dbTemplateInfo.dbTemplate.dbObjectSchemaName + '.' + this.dbTemplateInfo.dbTemplate.dbObjectName + '] (' + this.dbTemplateInfo.dbTemplate.dbTemplateGroupId + ')';

      this.allDbObjectItems.set(this.dbTemplateInfo.dbTemplateItems);
      this.filterDbObjectItems.set(this.dbTemplateInfo.dbTemplateItems);

      // Auto-select first item
      if (this.dbTemplateInfo.dbTemplateItems.length > 0) {
        this.selectedItem.set(this.dbTemplateInfo.dbTemplateItems[0]);
      }

      this.isEnabled = this.dbTemplateInfo.dbTemplate.enabled;
      // Default type helpers
      if (this.dbTemplateInfo.dbTemplateItems.length > 0) {
        this.dbTemplateInfo.dbTemplateItems.forEach(element => {

          element.defaultValueItems = this.templateService.getValueTypes(element.dbDataType);
          if (element.defaultValueType != null && element.defaultValueType != '') {
            if (element.defaultValueItems.indexOf(element.defaultValueType) != -1) {
              element.defaultValueItems.unshift(element.dbDataType);
            }
            else {
              let defaultvaluetype = element.defaultValueType;
              if (element.defaultValueTypeNonSql != null
                && element.defaultValueTypeNonSql != ''
                && element.defaultValueTypeNonSql != element.dbDataType) {
                defaultvaluetype = element.defaultValueTypeNonSql + ' [' + element.defaultValueType + ']';
              }
              element.defaultValueItems.unshift(defaultvaluetype);
            }
          }
          else {
            element.defaultValueItems.unshift(element.dbDataType);
          }

          // Systemfelt ?
          element.isSystemField = element.defaultValueType.indexOf(':') != -1;

          // nulstil fejlmeddelelse
          element.errorMessage = '';

          // default værdi formatering (ikke for systemfelter)
          if (!element.isSystemField) {
            if (element.defaultValue == null || element.defaultValue == '') {
              // General null value!
              if (element.dbIsNullable) {
                element.defaultValue = 'System: Null';
              }
              else {
                if (element.dbDataType == 'datetime' || element.dbDataType == 'datetime2') {
                  element.defaultValue = 'System: Nu';
                }
                else if (element.dbDataType == 'int' || element.dbDataType == 'bit') {
                  element.defaultValue = '0';
                }
                else if (element.dbDataType == 'decimal' || element.dbDataType == 'numeric') {
                  element.defaultValue = '0.0';
                }
                else if (element.dbDataType === 'nvarchar' || element.dbDataType === 'varchar') {
                  element.defaultValue = 'System: Tom streng';
                }
                else if (element.dbDataType === 'uniqueidentifier') {
                  element.defaultValue = 'System: Tom Guid';
                }
              }
            }
            else {
              if (element.dbDataType == 'int' || element.dbDataType == 'bit' || element.dbDataType == 'decimal' || element.dbDataType == 'numeric') {
                // Er det et tal ?
                const nr: number = Number(element.defaultValue);
                if (isNaN(nr) && element.defaultValue != null) {
                  element.defaultValue = element.defaultValue.replace('((', '').replace('))', '');
                }
              }
            }
          }

          switch (element.origin) {
            case DbObjectItemCreatedByEnum.Table:
              element.originName = 'Tabel';
              break;
            case DbObjectItemCreatedByEnum.View:
              element.originName = 'View';
              break;
            case DbObjectItemCreatedByEnum.StoredProcedure:
              element.originName = 'Stored Procedure';
              break;
            case DbObjectItemCreatedByEnum.SPInput:
              element.originName = 'SP Input';
              break;
            case DbObjectItemCreatedByEnum.SPOutput:
              element.originName = 'SP Output';
              break;
            case DbObjectItemCreatedByEnum.SPHybrid:
              element.originName = 'Hybrid (oprettet manuelt)';
              break;
            default:
              element.originName = 'Ikke sat';
              break;
          }
        });
      }
      this.rememberSavedFieldNames();
      this.templateWorkingText = '';
  }

  private rememberSavedFieldNames() {
    this.savedFieldNames = new Map(this.dbTemplateInfo.dbTemplateItems.map(item => [item.id, item.name]));
    this.savedState = this.currentState();
  }

  /** Ændringer på siden, der ikke er gemt — vist som en rød prik på "Gem skabelon". */
  isDirty(): boolean {
    return !this.isLoading && this.savedState !== '' && this.currentState() !== this.savedState;
  }

  // Kaldes af unsavedChangesGuard ved al navigation væk fra siden (tilbage-knappen, menuen, browserens tilbage).
  async confirmLeave(): Promise<boolean> {
    if (this.leaveWithoutCheck || !this.isDirty()) {
      return true;
    }
    return await this.commonService.confirm('Ugemte ændringer', 'Der er ændringer til skabelonen, som ikke er gemt. Vil du forlade siden uden at gemme?', 'Ja', 'Nej', 'lg').catch(() => false);
  }

  // Genindlæsning eller lukning af fanen går uden om routeren; browseren viser sin egen advarsel.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (!this.leaveWithoutCheck && this.isDirty()) {
      event.preventDefault();
    }
  }

  // Fejlbeskeder fra valideringen ved gem er ikke en ændring af skabelonen.
  private currentState(): string {
    return JSON.stringify(this.dbTemplateInfo, (key, value) => key === 'errorMessage' ? undefined : value);
  }

  // "Tjek databasetabel": sammenlign skabelonen med databaseobjektet og lad admin vælge, hvad der skal ske med
  // forskellene. Skabelonen genindlæses bagefter, så ugemte ændringer på siden går tabt — derfor spørges først.
  async onCheckDatabase() {
    if (!await this.confirmDiscardUnsaved('hvis skabelonen opdateres fra databasen')) {
      return;
    }

    const modalRef = this.ngmService.open(DbTemplateSchemaDiffComponent, { size: 'xl', backdrop: 'static', scrollable: true });
    modalRef.componentInstance.dbTemplateId.set(this.dbTemplateInfo.dbTemplate.id);
    modalRef.componentInstance.dbTemplateName.set(this.dbTemplateInfo.dbTemplate.displayName || this.dbTemplateInfo.dbTemplate.name);
    modalRef.result.then(() => this.reloadDbTemplate(), () => { });
  }

  // Opret/slet felt og "Tjek databasetabel" gemmer på serveren og genindlæser skabelonen bagefter, så ugemte
  // ændringer på siden ville gå tabt uden varsel.
  private async confirmDiscardUnsaved(when: string): Promise<boolean> {
    if (this.currentState() === this.savedState) {
      return true;
    }
    return await this.commonService.confirm('Ugemte ændringer', 'Du har ugemte ændringer i skabelonen. De går tabt, ' + when + '. Vil du fortsætte?', 'Ja', 'Nej', 'lg').catch(() => false);
  }

  private async reloadDbTemplate(selectItemId: string | null = null) {
    await this.GetDbTemplate(this.dbTemplateInfo.dbTemplate.id);
    this.isLoading = false;
    const item = selectItemId ? this.dbTemplateInfo.dbTemplateItems.find(x => x.id === selectItemId) : null;
    if (item) {
      this.selectedItem.set(item);
    }
  }

  isHybrid(item: DbTemplateItem): boolean {
    return item.origin == DbObjectItemCreatedByEnum.SPHybrid;
  }

  // Stored procedurens resultatsæt kan afhænge af IF/CASE, så admin kan oprette de kolonner, beskrivelsen ikke fandt.
  async onAddField() {
    if (!await this.confirmDiscardUnsaved('når feltet oprettes')) {
      return;
    }

    const modalRef = this.ngmService.open(DbTemplateFieldCreateComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.dbTemplateId.set(this.dbTemplateInfo.dbTemplate.id);
    modalRef.componentInstance.existingItems.set(this.dbTemplateInfo.dbTemplateItems);
    modalRef.result.then((id: string) => this.reloadDbTemplate(id), () => { });
  }

  async onDeleteField(item: DbTemplateItem) {
    if (!await this.confirmDiscardUnsaved('når feltet slettes')) {
      return;
    }

    const usedIn = await this.templateService.getdbTemplateUsedIn(this.dbTemplateInfo.dbTemplate.id);
    const usedInText = usedIn.length == 0
      ? 'Ingen afdelingsskabeloner bruger databaseskabelonen.'
      : 'Feltet fjernes også fra disse skabeloner:\n' + usedIn.map(x => '• ' + x.templateName + ' – ' + x.departmentName + (x.templateEnabled ? ' (aktiv)' : ' (inaktiv)')).join('\n');
    if (!await this.commonService.confirm('Slet felt', `Vil du slette feltet '${item.name}' (kolonne ${item.dbColumnName})?\n\n` + usedInText, 'Ja', 'Nej', 'lg').catch(() => false)) {
      return;
    }

    this.awaitingNetworkResponse = true;
    try {
      await this.templateService.deleteDbTemplateField(this.dbTemplateInfo.dbTemplate.id, item.id);
      this.toastrService.success(`Feltet '${item.name}' er slettet`);
      await this.reloadDbTemplate();
    } catch {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
    } finally {
      this.awaitingNetworkResponse = false;
    }
  }

  // Omdøbte felter føres ikke videre til brugerskabelonerne (deres navn er kontrakten med Business Central),
  // så admin skal se, hvilke skabeloner der har feltet, og bekræfte. Returnerer null, hvis intet felt er omdøbt.
  private async confirmRenamedFields(): Promise<boolean | null> {
    const renamed = this.dbTemplateInfo.dbTemplateItems.filter(item =>
      this.savedFieldNames.has(item.id) && this.savedFieldNames.get(item.id) !== item.name);
    if (renamed.length == 0) {
      return null;
    }

    const usedIn = await this.templateService.getdbTemplateUsedIn(this.dbTemplateInfo.dbTemplate.id);
    if (usedIn.length == 0) {
      return null;
    }

    const renamedText = renamed.map(item => '• ' + item.dbColumnName + ': ' + this.savedFieldNames.get(item.id) + ' → ' + item.name).join('\n');
    const usedInText = usedIn.map(x => '• ' + x.templateName + ' – ' + x.departmentName + (x.templateEnabled ? ' (aktiv)' : ' (inaktiv)')).join('\n');
    const message = 'Du har omdøbt følgende felter:\n' + renamedText
      + '\n\nNavnet ændres ikke i de eksisterende skabeloner. De beholder deres nuværende feltnavn, som Business Central fortsat skal sende. '
      + 'Følgende skabeloner har feltet:\n' + usedInText
      + '\n\nVil du fortsætte?';

    return await this.commonService.confirm('Feltnavn ændret', message, 'Ja', 'Nej', 'lg').catch(() => false);
  }

  // Helper tekster..
  setPropertiesInfo() {
    this.propertiesInfo = '';
    if (this.dbTemplateInfo.dbTemplate.allowRead) {
      this.propertiesInfo += 'R: [';

      if (this.dbTemplateInfo.dbTemplate.allowSynchronousCall) {
        this.propertiesInfo += 'direkte læsning/';
      }

      if (this.dbTemplateInfo.dbTemplate.allowReadAll) {
        this.propertiesInfo += 'uden filtre/';
      } else {
        this.propertiesInfo += 'filtre påkrævet/';        
      }

      if (this.dbTemplateInfo.dbTemplate.enableRecordReadLimit) {
         this.propertiesInfo += 'begrænset (' + this.dbTemplateInfo.dbTemplate.recordReadLimit + ')] ';
      } else {
         this.propertiesInfo += 'ubegrænset] ';
      }

      if (this.dbTemplateInfo.dbTemplate.preProcessingOnRead || this.dbTemplateInfo.dbTemplate.postProcessingOnRead) {
        this.propertiesInfo += ' Behandlinger [';
         if (this.dbTemplateInfo.dbTemplate.preProcessingOnRead) {
            this.propertiesInfo += 'før';
            if (this.dbTemplateInfo.dbTemplate.postProcessingOnRead) {
              this.propertiesInfo += ', efter]';
            } else {
              this.propertiesInfo += ']';
            } 
        } else {
          this.propertiesInfo += 'efter]';
        }
      }      
    }

    if (this.dbTemplateInfo.dbTemplate.allowWrite) {
      this.propertiesInfo += ' W';
      if (this.dbTemplateInfo.dbTemplate.preProcessingOnWrite || this.dbTemplateInfo.dbTemplate.postProcessingOnWrite) {
        this.propertiesInfo += ': Behandlinger [';
         if (this.dbTemplateInfo.dbTemplate.preProcessingOnWrite) {
            this.propertiesInfo += 'før';
            if (this.dbTemplateInfo.dbTemplate.postProcessingOnWrite) {
              this.propertiesInfo += ', efter]';
            } else {
              this.propertiesInfo += ']';
            } 
        } else {
          this.propertiesInfo += 'efter]';
        }
      }
      if (this.dbTemplateInfo.dbTemplate.isEos) {
        this.propertiesInfo += ' (EOS)';
      }
    }

    if (this.dbTemplateInfo.dbTemplate.allowDelete) {
      this.propertiesInfo += ' D';
      if (this.dbTemplateInfo.dbTemplate.preProcessingOnDelete || this.dbTemplateInfo.dbTemplate.postProcessingOnDelete) {
        this.propertiesInfo += ': Behandlinger [';
         if (this.dbTemplateInfo.dbTemplate.preProcessingOnDelete) {
            this.propertiesInfo += 'før';
            if (this.dbTemplateInfo.dbTemplate.postProcessingOnDelete) {
              this.propertiesInfo += ', efter]';
            } else {
              this.propertiesInfo += ']';
            } 
        } else {
          this.propertiesInfo += 'efter]';
        }
      }
    }

    const departmentPermissions: string[] = [];
    if (this.dbTemplateInfo.dbTemplate.departmentAllowCreate) {
      departmentPermissions.push('opret');
    }
    if (this.dbTemplateInfo.dbTemplate.departmentAllowEdit) {
      departmentPermissions.push('rediger');
    }
    if (this.dbTemplateInfo.dbTemplate.departmentAllowDelete) {
      departmentPermissions.push('slet');
    }
    this.propertiesInfo += ' Afdeling: [' + (departmentPermissions.length > 0 ? departmentPermissions.join(', ') : 'ingen') + ']';
  }

  // inputs..
  onDefaultValueTypeChange($event: Event, dbitem: DbTemplateItem) {
    dbitem.defaultValueType = ($event.target as HTMLSelectElement).value;
    dbitem.isSystemField = dbitem.defaultValueType.indexOf(':') != -1;
    if (dbitem.isSystemField) {
      // if (dbitem.templateItem.isMandatory)
      // {
      //   dbitem.templateItem.isMandatory = false;
      dbitem.allowCreate = false;
      dbitem.allowUpdate = false;
      dbitem.mandatory = false;
      dbitem.defaultValue = '';
      // }
    }
  }

  onAdministerGroups() {
  }
  
  onDefaultOption(dbitem: DbTemplateItem, value: string) {
    dbitem.defaultValue = value;
  }

  onDefaultOptionChange($event: Event, dbitem: DbTemplateItem) {
    dbitem.defaultValueType = ($event.target as HTMLSelectElement).value;
  }

  async onUpdateTemplate() {
    let anyErrors: number = 0;
    let angivetKundeId: boolean = false;
    let angivetRegnskabId: boolean = false;

    this.awaitingNetworkResponse = true;    
    try {
      // valider template items..
      this.dbTemplateInfo.dbTemplateItems.forEach(element => {
        element.errorMessage = '';
        // Er det et ikke systemfelt?
        if (!element.isSystemField) {
          if (element.defaultValue == null || element.defaultValue == '' && !element.dbIsNullable) {
            element.errorMessage = 'Mangler defaultværdi';
            anyErrors++;
          }
          else {
            if (element.defaultValue == 'System: Null' && element.dbIsNullable) {
              return;
            }

            switch (element.dbDataType) {
              case 'nvarchar':
              case "varchar":
                if (element.defaultValue.length == 0 && element.dbIsNullable == false) {
                  element.errorMessage = 'Mangler defaultværdi';
                  anyErrors++;
                }
                break;
              case 'int':
                if (element.defaultValue == 'System: Null') {
                  break;
                }

                if (!validator.isInt(element.defaultValue)) {
                  element.errorMessage = 'Skal være et heltal';
                  anyErrors++;
                }
                break;
              case 'uniqueidentifier':
                if (element.defaultValue == 'System: Ny Guid' || element.defaultValue == 'System: Ny UUID' || element.defaultValue == 'System: Tom Guid' || element.defaultValue == 'System: Null') {
                  break;
                }
                if (!validator.isUUID(element.defaultValue)) {
                  element.errorMessage = 'Skal være en GUID';
                  anyErrors++;

                }
                break;
              case 'datetime':
              case 'datetime2':
                if (element.defaultValue == 'System: Nu' || element.defaultValue == 'System: Null') {   
                  break;
                }
                if (!validator.isISO8601(element.defaultValue)) {
                  element.errorMessage = 'Skal være en dato';
                  anyErrors++;

                }
                break;
              case 'bit':
                if (!validator.isBoolean(element.defaultValue)) {
                  element.errorMessage = 'Skal være en boolean';
                  anyErrors++;

                }
                break;
              case 'float':
                if (!validator.isFloat(element.defaultValue)) {
                  element.errorMessage = 'Skal være et decimaltal';
                  anyErrors++;
                }
                break;
              case 'decimal':
                if (element.defaultValue == 'System: Null') {
                  break;
                }
                if (!validator.isDecimal(element.defaultValue)) {
                  element.errorMessage = 'Skal være et decimaltal';
                  anyErrors++;
                }
                break;
              default:
                break;
            }
          }
        }
        else
        {
          if (element.defaultValueType == 'Api: KundeId') {
            angivetKundeId = true;
          }
          if (element.defaultValueType == 'Api: RegnskabId') { 
            angivetRegnskabId = true;
          }
        }
      });

      let errstr = '';
      if (angivetKundeId == false) {
        errstr += 'kundeId';
      }
      if (angivetRegnskabId == false) {
        if (errstr != '') {
          errstr += ' og ';
        }
        errstr += 'regnskabId';
      }

      if (errstr != '') {
        if (! await this.commonService.confirm('Advarsel', 'Der er ikke angivet ' + errstr + ' i skabelonen. Vil du fortsætte?', 'Ja', 'Nej', 'lg')) {
          return;
        }
      }

      // valider ReadLimit!
      if (this.dbTemplateInfo.dbTemplate.recordReadLimit <= 0) {
        if (this.dbTemplateInfo.dbTemplate.enableRecordReadLimit) {
          this.errorService.notify('Læsebegrænsning skal være større end 0!');
        } else {
          this.errorService.notify('Læsebegrænsning skal være større end 0! (også selv om den er disabled)');
        }

        return;
      }

      if (anyErrors > 0) {
        this.notifyFieldErrors();
        return;
      }
      else {

        // Check for obligatorisk og nøgler!
        if (this.dbTemplateInfo.dbTemplateItems.some(item => item.mandatory && item.isKey) == false) {
          if (! await this.commonService.confirm('Advarsel: Nøglefelt(er) mangler!', 'Der er ingen nøgle til et eller flere obligatoriske felter. Vil du fortsætte?', 'Ja', 'Nej', 'lg')) {
            return;
          }
        }

        // Omdøbte felter? Vis påvirkede brugerskabeloner (erstatter den generelle bekræftelse nedenfor).
        const renamedConfirmed = await this.confirmRenamedFields();
        if (renamedConfirmed === false) {
          return;
        }

        // Er den enabled ?
        if (this.isEnabled && renamedConfirmed === null) {

          if (! await this.commonService.confirm('Opdater databaseskabelon', 'Er du sikker på at du vil opdatere skabelonen? - (ændringer kan påvirke bruger-skabeloner.)', 'Ja', 'Nej', 'lg')) {
            return;
          }
        }

        this.templateWorkingText = 'Opdater skabelon..';
        this.templateService.updateDbTemplate(this.dbTemplateInfo).then(x => {
          this.rememberSavedFieldNames();
          this.toastrService.success('Databaseskabelonen er opdateret');
          this.templateWorkingText = '';
          this.isEnabled = this.dbTemplateInfo.dbTemplate.enabled;
        })
        .catch(() => {
          // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
        })
        .finally(() => {  
          this.templateWorkingText = '';
          this.awaitingNetworkResponse = false;
        });
      }
    } catch (error) {
      if (error) {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      }
    } finally {
      this.awaitingNetworkResponse = false;
    }
  }

  // The field list only shows an icon per error, and a filtered list may hide the field, so name each one here.
  private notifyFieldErrors() {
    const failed = this.dbTemplateInfo.dbTemplateItems.filter(item => item.errorMessage);
    const lines = failed.map(item => `• ${this.showNames(item)} (${item.dbDataType}): ${item.errorMessage}`
      + (item.defaultValue ? ` – defaultværdi '${item.defaultValue}'` : ''));
    this.errorService.notify(
      `Skabelonen kunne ikke gemmes. ${failed.length == 1 ? 'Ret dette felt' : `Ret disse ${failed.length} felter`}:\n` + lines.join('\n'),
      { title: 'Ugyldige felter' });

    if (failed.length > 0) {
      this.searchText.set('');
      this.filterDbObjectItems.set(this.allDbObjectItems());
      this.selectedItem.set(failed[0]);
    }
  }

  onGoBack() {
    this.router.navigate(['/db-templates']);
  }
  
  onDeletetemplate() {
    try {
      this.awaitingNetworkResponse = true;
      const modalRef = this.ngmService.open(DbTemplateDeleteComponent, { size: 'lg', backdrop: 'static' });
      modalRef.componentInstance.DbTemplate = this.dbTemplateInfo.dbTemplate;
      modalRef.result.then(async x => { this.leaveWithoutCheck = true; this.router.navigate(['/db-templates']);
      }, y => { });
    } catch (error) {
      if (error) {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      }
    } finally {
      this.awaitingNetworkResponse = false;
    }
  }

  public onDisable() {
    if (this.dbTemplateInfo.dbTemplate.enabled) {
      this.commonService.confirm('Deaktiver skabelon', 'Skabelonen vil ikke være aktiv/synlig for bruger-skabeloner! Vil du deaktivere skabelonen?', 'Ja', 'Nej', 'lg')
        .then((confirmed) => {
          if (confirmed) {
            this.dbTemplateInfo.dbTemplate.enabled = false;
          }
          else { this.dbTemplateInfo.dbTemplate.enabled = true; }
        })
        .catch(() => { this.dbTemplateInfo.dbTemplate.enabled = true; });
    }
  }

  onMandatoryChange($event: any, item: DbTemplateItem) {
    if ($event.target.checked == false) {
      item.isKey = false;
    }
  }

  onShowFilter() {
    const modalRef = this.ngmService.open(DbTemplateFilterComponent, { size: 'lg', backdrop: 'static', keyboard: false });
    modalRef.componentInstance.dbTemplateInfo = this.dbTemplateInfo;
    modalRef.result.then(async x => { }, y => { });

  }

  onShowtemplateinfo() {
    const modalRef = this.ngmService.open(DbTemplateInfoComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.dbTemplateInfo = this.dbTemplateInfo;
    modalRef.result.then(async x => { }, y => { });
  }

  onShowProperties() {
    const modalRef = this.ngmService.open(DbTemplatePropertiesComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.DbTemplateInfo = this.dbTemplateInfo;
    modalRef.componentInstance.postProcessChoicesWrite = this.postProcessChoicesWrite;
    modalRef.componentInstance.postProcessChoicesDelete = this.postProcessChoicesDelete;
    modalRef.result.then(async x => { this.setPropertiesInfo(); }, y => { this.setPropertiesInfo(); });
  }

  onFilteredItems(filtered: DbTemplateItem[]): void {
    this.filterDbObjectItems.set(filtered);
    // Re-select if current selection is no longer in filtered list
    const current = this.selectedItem();
    if (current && !filtered.includes(current)) {
      this.selectedItem.set(filtered.length > 0 ? filtered[0] : null);
    } else if (!current && filtered.length > 0) {
      this.selectedItem.set(filtered[0]);
    }
  }

  selectItem(item: DbTemplateItem): void {
    this.selectedItem.set(item);
  }

  showNames(item: DbTemplateItem): string {
    let _name = item.dbColumnName;
    if (item.name != null && item.name != '' && item.name != item.dbColumnName) {
      _name += ' [' + item.name + ']';
    }
    return _name;
  }

  fastAction(event: any): void {
    this.router.navigate(['/db-template', event.id]);
  }

  ShowDbTemplate(tableId: string): void {
    this.router.navigate(['/db-template', tableId ]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }  
}
